import { LoadingContent, Spinner } from '../components/feedback';
import { EdgeIpWarning } from '../components/EdgeIpWarning';
import { useState } from 'react';
import { Link } from 'react-router';
import { api3 } from '../api/client-p3';
import { errorMessage, useApp } from '../app-context';
import { Badge, Empty, ErrorAlert } from '../components/ui';
import { formatDateTime } from '../format';
import { usePoll } from '../hooks/usePoll';
import { EdgeWarning } from '../components/EdgeWarning';

const kindLabel: Record<string, string> = {
  velocity: 'Velocity（hub 経由）',
  direct: '直接接続',
  panel: '管理パネル',
  mcp: 'AI 用 MCP',
  web: 'Web サイト',
};

const edgeState = {
  applied: { label: '反映済み', tone: 'ok' as const },
  pending: { label: '未反映の変更あり', tone: 'warn' as const },
  error: { label: '反映に失敗', tone: 'danger' as const },
  unknown: { label: 'まだ反映していません', tone: 'warn' as const },
};

// 編集できる項目がないので編集ロック（編集ボタン）は置かない
export function Routing() {
  const { canOperate, notify } = useApp();
  const routing = usePoll(() => api3.routing(), 15000);
  const [busy, setBusy] = useState(false);
  const data = routing.data;

  const apply = async () => {
    setBusy(true);
    try {
      await api3.applyEdge();
      await routing.reload();
      notify('edge に反映しました');
    } catch (error) {
      notify(errorMessage(error), true);
      void routing.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1 data-search-id="routing">接続先</h1>
          <p>接続先の変更は自動で edge に反映します。Velocity の接続先変更では、再起動により参加者が切断されます。</p>
        </div>
        {/* 初回（unknown）や失敗後の再試行でも反映できるようにする */}
        {canOperate && data && data.edge.state !== 'applied' && (
          <button className="btn primary" disabled={busy} onClick={() => void apply()}>
            {busy && <Spinner />}
            {busy ? '反映中…' : '今すぐ反映'}
          </button>
        )}
      </div>
      <ErrorAlert error={routing.error} />
      <EdgeWarning routing={data} />
      <EdgeIpWarning edge={data?.edge} />
      <LoadingContent loading={!data && routing.loading}>
        {data && (
          <>
            {data.hub !== undefined && (
              <div className="card card-body stack">
                <div>
                  <span className="small muted">最初に入るサーバー</span>{' '}
                  <Link to={`/servers/${encodeURIComponent(data.hub)}`}>
                    <strong>{data.hub}</strong>
                  </Link>
                </div>
                <p className="small" style={{ margin: 0 }}>
                  Mod なしのサーバー（Velocity 配下）は、<code>{data.domain}</code>・<code>play.{data.domain}</code>・
                  <code>名前.{data.domain}</code> のどれで入っても、まず {data.hub} に入ります。ほかのサーバーへは hub
                  のポータルか <code>/server 名前</code> で移ります。
                </p>
              </div>
            )}
            <div className="card card-body row">
              <Badge tone={edgeState[data.edge.state].tone}>{edgeState[data.edge.state].label}</Badge>
              {data.edge.appliedAt && (
                <span className="small muted">最終反映 {formatDateTime(data.edge.appliedAt)}</span>
              )}
              {data.edge.message && <span className="small">{data.edge.message}</span>}
            </div>
            <div className="card">
              {data.entries.length === 0 ? (
                <Empty>転送先はまだありません。</Empty>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>ホスト名</th>
                        <th>種類</th>
                        <th>転送先</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.entries.map((entry) => (
                        <tr key={entry.hostname + entry.target}>
                          <td className="mono">{entry.hostname}</td>
                          <td className="small">{kindLabel[entry.kind] ?? entry.kind}</td>
                          <td className="small">
                            {entry.serverId ? (
                              <Link to={`/servers/${encodeURIComponent(entry.serverId)}`}>{entry.target}</Link>
                            ) : (
                              entry.target
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <p className="small faint" style={{ margin: 0 }}>
              Mod 入りのサーバーは Velocity を通さず、<code>名前.{data.domain}</code> で直接つなぎます。
            </p>
          </>
        )}
      </LoadingContent>
    </div>
  );
}
